import React, { useState, useEffect } from 'react';
import { Shield, Key, Copy, Check, AlertCircle, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/v2/api';

const TwoFactorSetup = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState('check'); // check, generate, verify, backup, disabled
  const [enabled, setEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const authToken = localStorage.getItem('hb9g_token');
      const res = await axios.get(`${API_URL}/2fa/status`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setEnabled(res.data.enabled);
      setStep(res.data.enabled ? 'disabled' : 'generate');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la vérification du statut 2FA');
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const authToken = localStorage.getItem('hb9g_token');
      const res = await axios.post(`${API_URL}/2fa/generate`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setQrCode(res.data.qrCode);
      setSecret(res.data.secret);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la génération du QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const authToken = localStorage.getItem('hb9g_token');
      const res = await axios.post(`${API_URL}/2fa/enable`, { token }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setBackupCodes(res.data.backupCodes);
      setStep('backup');
      setEnabled(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const authToken = localStorage.getItem('hb9g_token');
      await axios.post(`${API_URL}/2fa/disable`, { password }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setEnabled(false);
      setStep('generate');
      if (onSuccess) onSuccess('2FA désactivé avec succès');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la désactivation');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleFinish = () => {
    if (onSuccess) onSuccess('2FA activé avec succès');
    if (onClose) onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}} onClick={onClose}>
      <div style={{backgroundColor:'white',borderRadius:14,width:'100%',maxWidth:520,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.2)'}} onClick={e=>e.stopPropagation()}>
        
        {/* Header */}
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:40,height:40,borderRadius:'50%',backgroundColor:'#EFF6FF',display:'flex',alignItems:'center',justifyContent:'center'}}>
              <Shield style={{width:20,height:20,color:'#2563EB'}}/>
            </div>
            <div>
              <h3 style={{fontWeight:800,fontSize:'1.0625rem',color:'#111827',margin:0}}>Authentification à deux facteurs</h3>
              <p style={{fontSize:'0.8125rem',color:'#6B7280',margin:'2px 0 0'}}>Sécurisez votre compte admin</p>
            </div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4}}>
            <X style={{width:20,height:20}}/>
          </button>
        </div>

        {/* Body */}
        <div style={{padding:'24px'}}>
          {error && (
            <div style={{backgroundColor:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:'12px 14px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
              <AlertCircle style={{width:16,height:16,color:'#DC2626',flexShrink:0}}/>
              <span style={{fontSize:'0.875rem',color:'#991B1B'}}>{error}</span>
            </div>
          )}

          {/* Step: Generate */}
          {step === 'generate' && (
            <div style={{textAlign:'center'}}>
              <p style={{fontSize:'0.875rem',color:'#374151',lineHeight:1.6,marginBottom:20}}>
                L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte. 
                Vous aurez besoin d'une application d'authentification comme <strong>Google Authenticator</strong> ou <strong>Authy</strong>.
              </p>
              <button onClick={handleGenerate} disabled={loading} style={{padding:'10px 20px',borderRadius:8,backgroundColor:'#2563EB',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:600,width:'100%'}}>
                {loading ? 'Génération...' : 'Activer le 2FA'}
              </button>
            </div>
          )}

          {/* Step: Verify */}
          {step === 'verify' && (
            <div>
              <div style={{textAlign:'center',marginBottom:20}}>
                <p style={{fontSize:'0.875rem',color:'#374151',marginBottom:16}}>
                  Scannez ce QR code avec votre application d'authentification :
                </p>
                {qrCode && <img src={qrCode} alt="QR Code" style={{width:200,height:200,margin:'0 auto',border:'2px solid #E5E7EB',borderRadius:8}}/>}
                <p style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:12}}>
                  Ou entrez manuellement : <code style={{backgroundColor:'#F3F4F6',padding:'2px 6px',borderRadius:4,fontSize:'0.7rem'}}>{secret}</code>
                </p>
              </div>
              <form onSubmit={handleEnable}>
                <label style={{display:'block',fontSize:'0.8125rem',fontWeight:600,color:'#374151',marginBottom:6}}>
                  Code de vérification
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={e=>setToken(e.target.value.replace(/\s/g,''))}
                  placeholder="000000"
                  maxLength={6}
                  required
                  style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:8,fontSize:'0.875rem',marginBottom:16,textAlign:'center',letterSpacing:'0.3em',fontFamily:'monospace',fontSize:'1.125rem'}}
                />
                <button type="submit" disabled={loading || token.length !== 6} style={{padding:'10px 20px',borderRadius:8,backgroundColor:'#2563EB',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:600,width:'100%',opacity:loading||token.length!==6?0.5:1}}>
                  {loading ? 'Vérification...' : 'Vérifier et activer'}
                </button>
              </form>
            </div>
          )}

          {/* Step: Backup codes */}
          {step === 'backup' && (
            <div>
              <div style={{backgroundColor:'#FEF9C3',border:'1px solid #FDE047',borderRadius:8,padding:'12px 14px',marginBottom:16}}>
                <p style={{fontSize:'0.8125rem',color:'#713F12',margin:0,fontWeight:600}}>
                  ⚠️ Sauvegardez ces codes de secours dans un endroit sûr
                </p>
              </div>
              <p style={{fontSize:'0.875rem',color:'#374151',marginBottom:16}}>
                Ces codes vous permettront de vous connecter si vous perdez l'accès à votre application d'authentification. 
                Chaque code ne peut être utilisé qu'une seule fois.
              </p>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:20}}>
                {backupCodes.map((code, i) => (
                  <div key={i} style={{backgroundColor:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:6,padding:'8px 10px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <code style={{fontSize:'0.8125rem',fontFamily:'monospace',color:'#111827'}}>{code}</code>
                    <button onClick={()=>copyCode(code,i)} style={{background:'none',border:'none',cursor:'pointer',color:'#6B7280',padding:2}}>
                      {copiedCode === i ? <Check style={{width:14,height:14,color:'#059669'}}/> : <Copy style={{width:14,height:14}}/>}
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={handleFinish} style={{padding:'10px 20px',borderRadius:8,backgroundColor:'#059669',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:600,width:'100%'}}>
                J'ai sauvegardé mes codes
              </button>
            </div>
          )}

          {/* Step: Disabled (already enabled) */}
          {step === 'disabled' && (
            <div>
              <div style={{backgroundColor:'#D1FAE5',border:'1px solid #6EE7B7',borderRadius:8,padding:'12px 14px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
                <Check style={{width:16,height:16,color:'#059669',flexShrink:0}}/>
                <span style={{fontSize:'0.875rem',color:'#065F46',fontWeight:600}}>Le 2FA est actuellement activé</span>
              </div>
              <p style={{fontSize:'0.875rem',color:'#374151',marginBottom:16}}>
                Pour désactiver l'authentification à deux facteurs, entrez votre mot de passe :
              </p>
              <form onSubmit={handleDisable}>
                <label style={{display:'block',fontSize:'0.8125rem',fontWeight:600,color:'#374151',marginBottom:6}}>
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  required
                  style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:8,fontSize:'0.875rem',marginBottom:16}}
                />
                <button type="submit" disabled={loading} style={{padding:'10px 20px',borderRadius:8,backgroundColor:'#DC2626',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:600,width:'100%'}}>
                  {loading ? 'Désactivation...' : 'Désactiver le 2FA'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetup;
